import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  canAuthorizeVisitors,
  isDraft,
} from "../../../shared/connectors/isDraft";
import { withoutListCatalogs } from "../../../shared/connectors/listRows";
import { sharedMutators } from "../../../shared/mutators";
import { GRANT_REQUEST_NOTE_MAX_LENGTH } from "../../../shared/grantRequests";
import { DeclineGrantRequestModal } from "../DeclineGrantRequestModal";
import { NoteInput } from "../NoteInput";
import { queries } from "../../../shared/queries";
import type { GrantEntry } from "../../../shared/rbac/roles";
import { diffGrants } from "../../../shared/rbac/validation/grantContent";
import { useHasAccess } from "../../hooks/useHasAccess";
import {
  connectorVariants,
  resolveConnectorConfig,
} from "../../lib/connectorDirectory";
import { connectorRawTypeLabel } from "../../lib/connector";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { AccessMapPanel } from "../AccessMapPanel";
import { ConnectorStatusPill } from "../ConnectorStatusPill";
import { Button } from "../Button";
import { Banner } from "../Banner";
import { ConfirmModal } from "../ConfirmModal";
import { DeleteConnectorModalBody } from "../Connectors/DeleteConnectorModal";
import { DangerSection } from "../Settings/DangerSection";
import { Icon } from "../Icon";
import { ConnectorHeading, Header } from "./Header";
import { Modal } from "../Modal";
import {
  useScopeAccessCloseWarning,
  WarningContent,
  WarningFooter,
} from "../ScopeAccessCloseWarning";
import { TabLinkButton } from "../TabButton";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import { WavyLine } from "../WavyLine";
import { AccessTab } from "./AccessTab";
import { Form } from "./Form";
import { SecureBadge } from "./SecureBadge";
import { UsageTab } from "./UsageTab";
import { useConnectorAuthorize } from "./useConnectorAuthorize";
import { VariantPicker } from "./VariantPicker";
import { VisitorAuthorizationSection } from "./VisitorAuthorizationSection";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { VISITOR_CONNECTOR_GRANTS_FLAG } from "../../../shared/featureFlagNames";

type Tab = "usage" | "settings" | "access";

const Body = styled("div")`
  padding: 16px;
  flex: 1;
  border: 0.5px solid var(--border-alpha);
  border-left: 0;
  border-right: 0;
  overflow: auto;
`;

const Footer = styled("div")`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterRight = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tabs = styled("div")`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const HeaderRight = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const Guide = styled("div")`
  opacity: 0.3;
  display: flex;
  align-items: center;
  gap: 4px;
`;

type Props = {
  id: string;
  onClose: () => void;
};

export function ConnectorModal({ id, onClose }: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [appRow, appResult] = useQuery(
    queries.loadConnectorDetails({ connectedAppId: id })
  );
  // Server hydration can momentarily drop the row while grants and relations
  // land across syncs; hold the last seen row so the modal never blanks.
  const [heldApp, setHeldApp] = useState<{
    id: string;
    app: NonNullable<typeof appRow>;
  } | null>(null);
  if (appRow && heldApp?.app !== appRow) setHeldApp({ id, app: appRow });
  const app = appRow ?? (heldApp?.id === id ? heldApp.app : undefined);

  const appConfig = resolveConnectorConfig(app?.identifier);
  const hasSettings = appConfig != null;

  const draft = !app || isDraft(app);

  const { tab } = useCurrentUrl(routes.modal.connector, { id });
  const rawTab = tab as Tab;
  const activeTab: Tab =
    !hasSettings && rawTab === "settings" ? "usage" : rawTab;
  const setActiveTab = (next: Tab) =>
    navigate(routes.modal.connector, { id, tab: next });
  const [visitedAccess, setVisitedAccess] = useState(activeTab === "access");
  if (activeTab === "access" && !visitedAccess) setVisitedAccess(true);
  const canWrite = useHasAccess("connected_app:write", app?.id);
  const visitorGrantsEnabled = useFeatureFlag(VISITOR_CONNECTOR_GRANTS_FLAG);
  const canShare = useHasAccess("connected_app:share", app?.id);

  // The fetched model catalog is scratch for picking, persisted only so the
  // async fetch can sync it to the client; drop it when the modal closes so it
  // doesn't linger in stored config. Skipped on the paths that remove the
  // connector, since there'd be no row left to write. This can't guarantee a
  // clean picker on reopen — a refresh in flight at close lands afterward and
  // re-adds catalog rows — so the reopen guarantee lives in the open clear below.
  const catalogCleanup = useRef<{
    id: string;
    name: string;
    identifier: string;
    urlPattern: string;
    configuration: Record<string, string>;
    canWrite: boolean;
  } | null>(null);
  const skipCatalogCleanup = useRef(false);
  useEffect(() => {
    if (!app) return;
    catalogCleanup.current = {
      id: app.id,
      name: app.name,
      identifier: app.identifier,
      urlPattern: app.urlPattern,
      configuration: app.configuration,
      canWrite,
    };
  });
  useEffect(
    () => () => {
      const latest = catalogCleanup.current;
      if (
        skipCatalogCleanup.current ||
        !latest?.canWrite ||
        !latest.name.trim()
      )
        return;
      const configuration = withoutListCatalogs(latest.configuration);
      if (
        Object.keys(configuration).length ===
        Object.keys(latest.configuration).length
      )
        return;
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: latest.id,
          name: latest.name,
          identifier: latest.identifier,
          urlPattern: latest.urlPattern,
          configuration,
        })
      );
    },
    [z]
  );
  // Strip any leftover catalog once when the connector opens, before this
  // session fetches its own — the race-free point to guarantee a clean picker,
  // regardless of a refresh that landed after a previous close.
  const catalogClearedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!app || !canWrite || !app.name.trim()) return;
    if (catalogClearedFor.current === app.id) return;
    catalogClearedFor.current = app.id;
    const configuration = withoutListCatalogs(app.configuration);
    if (
      Object.keys(configuration).length ===
      Object.keys(app.configuration).length
    )
      return;
    z.mutate(
      sharedMutators.updateConnectedApp({
        connectedAppId: app.id,
        name: app.name,
        identifier: app.identifier,
        urlPattern: app.urlPattern,
        configuration,
      })
    );
  }, [app, canWrite, z]);
  const [guideOn, setGuideOn] = useState(draft);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [authorizePrompt, setAuthorizePrompt] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [currentUser] = useQuery(queries.currentUser());

  // Secret values never replicate back to the client, so which masked fields
  // were filled this session is tracked here — the modal outlives the tab
  // switches that unmount the settings form. Switching variant clears the
  // row's secrets, so forget them then too.
  const [locallySet, setLocallySet] = useState<ReadonlySet<string>>(new Set());
  const [requestGrants, setRequestGrants] = useState<GrantEntry[] | null>(null);
  // Whether a reviewer has actually changed the proposed grants. Approval only
  // sends its edited set when this is true, so an unedited (or not-yet-seeded)
  // approval falls back to the stored proposal rather than an empty/stale set.
  const [requestGrantsEdited, setRequestGrantsEdited] = useState(false);
  const [requestNote, setRequestNote] = useState("");
  const [decliningRequest, setDecliningRequest] = useState<string | null>(null);
  const onFieldSet = useCallback((key: string) => {
    setLocallySet((prev) => (prev.has(key) ? prev : new Set([...prev, key])));
  }, []);
  const identifier = app?.identifier;
  // Switching connector or variant clears the session-local secret and grant
  // state; adjusted during render so the old connector's values never paint.
  const [resetKey, setResetKey] = useState<string>(`${id}:${identifier}`);
  if (resetKey !== `${id}:${identifier}`) {
    setResetKey(`${id}:${identifier}`);
    setLocallySet(new Set());
    setRequestGrants(null);
    setRequestGrantsEdited(false);
    setRequestNote("");
    setDecliningRequest(null);
  }

  const auth = useConnectorAuthorize({
    app,
    definition: appConfig?.definition,
    locallySet,
    onEdit: () => setIsDirty(true),
  });

  const [pendingRequest, pendingRequestResult] = useQuery(
    queries.grantRequestForScope({ scopeId: id })
  );
  const canRequestShare = canWrite && !canShare;
  const isRequester =
    pendingRequest != null &&
    pendingRequest.requestedByUserId === currentUser?.id;
  const showPendingReview = pendingRequest != null;
  const showCompose = canRequestShare && pendingRequest == null;
  const requestMode = showPendingReview || showCompose;

  // Seed the local grant set once: from the pending request's proposal if one
  // exists, otherwise from the connector's current grants (the requester's own),
  // so the submitted request carries the full desired set and approving it via
  // patchScopeGrants doesn't strip the creator's access. Wait for the request
  // query to finish so a not-yet-synced relation can't seed an empty set.
  if (
    requestMode &&
    requestGrants === null &&
    !(pendingRequest && pendingRequestResult.type !== "complete")
  ) {
    const source: readonly {
      userGroupId?: string | null;
      userId?: string | null;
      serviceAccountId?: string | null;
      builtinRoleId?: string | null;
      customRoleId?: string | null;
    }[] = pendingRequest
      ? pendingRequest.requestedGrants
      : (app?.roleGrantsAsScope ?? []);
    setRequestGrants(
      source
        .map((g) => ({
          userGroupId: g.userGroupId ?? undefined,
          userId: g.userId ?? undefined,
          serviceAccountId: g.serviceAccountId ?? undefined,
          builtinRoleId: g.builtinRoleId ?? undefined,
          customRoleId: g.customRoleId ?? undefined,
        }))
        .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
    );
  }

  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({
      singular: "member or group",
      plural: "members and groups",
    });

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const autoFocusedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!draft || autoFocusedFor.current === id) return;
    const body = bodyRef.current;
    if (!body) return;
    requestAnimationFrame(() => {
      const input = body.querySelector<HTMLInputElement>(
        'input[type="text"]:not([readonly])'
      );
      if (input) {
        input.focus({ preventScroll: true });
        body.scrollTop = 0;
        autoFocusedFor.current = id;
      }
    });
  }, [draft, id]);

  if (!app) {
    if (appResult?.type !== "complete") {
      return (
        <Modal
          open
          onClose={onClose}
          style={{
            width: 680,
            height: 760,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <WavyLine />
        </Modal>
      );
    }
    return null;
  }

  if (!hasSettings && draft) {
    return null;
  }

  const unsaved = canWrite && app.fresh;

  const grantType = appConfig?.definition.oauth?.grantType(app.configuration);
  const clientCredentials = grantType === "client_credentials";
  const passwordGrant = grantType === "password";
  const visitorAuthorizationSupported = canAuthorizeVisitors(app);
  const showVisitorAuthorization =
    app.authorizationMode === "visitor" ||
    (visitorGrantsEnabled && visitorAuthorizationSupported);

  const trimmedName = app.name?.trim();
  const typeLabel = connectorRawTypeLabel(app);
  const showType = Boolean(trimmedName) && trimmedName !== typeLabel;

  const discardDraft = () => {
    skipCatalogCleanup.current = true;
    z.mutate(
      sharedMutators.tidyUpFreshConnectedApp({
        connectedAppId: app.id,
      })
    );
    onClose();
  };

  const dismissModal = () => {
    if (!unsaved) {
      onClose();
      return;
    }
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    discardDraft();
  };

  // reportValidity can't flash its hint on a field in a hidden tab.
  const formIsValid = () => {
    if (hasSettings && activeTab !== "settings") {
      flushSync(() => setActiveTab("settings"));
    }
    return bodyRef.current?.querySelector("form")?.reportValidity() ?? true;
  };

  const publishAndClose = () => {
    z.mutate(sharedMutators.publishConnectedApp({ connectedAppId: app.id }));
    onClose();
  };

  const saveAndClose = () => {
    const name = app.name?.trim() ? app.name : fallbackName();
    const urlPattern = app.urlPattern || fallbackUrlPattern();

    if (name !== app.name || urlPattern !== app.urlPattern) {
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: app.id,
          name,
          identifier: app.identifier,
          urlPattern,
          configuration: app.configuration,
        })
      );
    }
    if (auth.canAuthorize) {
      // Client-credentials connections need no consent step, so connect in
      // the background; only flows that open a popup get the prompt.
      if (clientCredentials) {
        auth.authorize();
      } else if (!passwordGrant) {
        setAuthorizePrompt(true);
        return;
      }
    }
    publishAndClose();
  };

  const authorizeAndClose = async () => {
    setAuthorizePrompt(false);
    if ((await auth.authorize()) === "error") return;
    publishAndClose();
  };

  const fallbackName = () => {
    const firstName = currentUser?.name?.split(" ")[0] || currentUser?.email;
    return firstName && app.identifier === "apiKey" && appConfig
      ? `${firstName}'s ${appConfig.name}`
      : (appConfig?.name ?? app.name);
  };

  const fallbackUrlPattern = () =>
    appConfig?.definition.defaultUrlPattern(app.configuration ?? {}) ?? "";

  // A fresh draft discards on close but must validate on save; a saved
  // connector is only worth checking once the user has actually edited it.
  const close = () => {
    if (canWrite && isDirty && !app.fresh && !formIsValid()) return;
    requestClose(dismissModal);
  };
  const finish = () => {
    if (canWrite && (isDirty || app.fresh) && !formIsValid()) return;
    requestClose(canWrite && app.fresh ? saveAndClose : dismissModal);
  };

  const goToAccess = () => {
    if (formIsValid()) setActiveTab("access");
  };

  // A reviewer who can share the connector may edit the proposed roles (remove,
  // change, or add) before approving — the same authority that gates Approve.
  // The requester viewing their own pending request stays read-only (they
  // cancel and re-request instead).
  const accessCanManage =
    showCompose ||
    (!requestMode && canShare) ||
    (showPendingReview && !isRequester && canShare);

  // Flag the principals the request would ADD (in the proposed set but not in the
  // connector's current grants) with a "New" badge — so both the requester
  // composing and the reviewer approving see the delta, not one flat list.
  const newPrincipalIds = (() => {
    if (!requestMode) return undefined;
    const current = new Set(
      (app.roleGrantsAsScope ?? []).flatMap((g) =>
        [g.userId, g.userGroupId, g.serviceAccountId].filter(
          (x): x is string => x != null
        )
      )
    );
    return new Set(
      (requestGrants ?? [])
        .map((g) => g.userId ?? g.userGroupId ?? g.serviceAccountId)
        .filter((id): id is string => id != null && !current.has(id))
    );
  })();

  const requestDelta = diffGrants(
    app.roleGrantsAsScope ?? [],
    requestGrants ?? []
  );
  const proposesShare = requestDelta.toInsert.length > 0;

  const submitRequest = (grants = requestGrants ?? []) => {
    if (app.fresh && !formIsValid()) return;
    const name = app.name?.trim() ? app.name : fallbackName();
    const urlPattern = app.urlPattern || fallbackUrlPattern();
    if (app.fresh && (name !== app.name || urlPattern !== app.urlPattern)) {
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: app.id,
          name,
          identifier: app.identifier,
          urlPattern,
          configuration: app.configuration,
        })
      );
    }
    if (app.fresh) {
      z.mutate(sharedMutators.publishConnectedApp({ connectedAppId: app.id }));
    }
    z.mutate(
      sharedMutators.requestScopeRoleGrants({
        requestId: nanoid(12),
        scopeId: app.id,
        note: requestNote.trim() || undefined,
        grants: grants.map((g) => ({
          userGroupId: g.userGroupId,
          userId: g.userId,
          serviceAccountId: g.serviceAccountId,
          builtinRoleId: g.builtinRoleId,
          customRoleId: g.customRoleId,
        })),
      })
    );
    onClose();
  };

  const cancelRequest = () => {
    if (pendingRequest) {
      z.mutate(
        sharedMutators.cancelGrantRequest({ requestId: pendingRequest.id })
      );
    }
    onClose();
  };

  const approveRequest = () => {
    if (pendingRequest) {
      z.mutate(
        sharedMutators.approveGrantRequest({
          requestId: pendingRequest.id,
          // Only send the reviewer's edits; an untouched approval omits grants
          // so the server applies the stored proposal as submitted.
          ...(requestGrantsEdited
            ? {
                grants: (requestGrants ?? []).map((g) => ({
                  userGroupId: g.userGroupId,
                  userId: g.userId,
                  serviceAccountId: g.serviceAccountId,
                  builtinRoleId: g.builtinRoleId,
                  customRoleId: g.customRoleId,
                })),
              }
            : {}),
        })
      );
    }
    onClose();
  };

  const requesterName =
    pendingRequest?.requestedByUser?.name ||
    pendingRequest?.requestedByUser?.email ||
    "someone";

  return (
    <>
      <Modal
        open
        onClose={close}
        style={{
          width: 680,
          height: 760,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header>
          <ConnectorHeading
            app={app}
            title={trimmedName || typeLabel}
            subtitle={showType ? typeLabel : undefined}
          />
          <Tabs>
            {hasSettings && (
              <TabLinkButton
                to={makeUrl(routes.modal.connector, {
                  id,
                  tab: "settings",
                })}
                className={activeTab === "settings" ? "active" : undefined}
              >
                <Icon name="settings-2" size={16} />
                <Text variant="text-small-md">Settings</Text>
              </TabLinkButton>
            )}
            <TabLinkButton
              to={makeUrl(routes.modal.connector, { id, tab: "access" })}
              className={activeTab === "access" ? "active" : undefined}
            >
              <Icon name="lock" size={16} />
              <Text variant="text-small-md">Access</Text>
            </TabLinkButton>
            {!draft && (
              <TabLinkButton
                to={makeUrl(routes.modal.connector, { id, tab: "usage" })}
                className={activeTab === "usage" ? "active" : undefined}
              >
                <Icon name="chart-no-axes-column" size={16} />
                <Text variant="text-small-md">Usage</Text>
              </TabLinkButton>
            )}
          </Tabs>
          <HeaderRight>
            {activeTab === "settings" && (
              <>
                <Guide>
                  <Text variant="text-small-md">Guide</Text>
                </Guide>
                <Toggle
                  isOn={guideOn}
                  onToggle={() => setGuideOn((was) => !was)}
                  disabled={!canWrite}
                />
                <Icon name="slash" size={12} color="var(--border-alpha)" />
              </>
            )}
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </HeaderRight>
        </Header>
        <Body ref={bodyRef} onChange={() => setIsDirty(true)}>
          {activeTab === "usage" ? (
            <UsageTab
              connectedAppId={id}
              loading={appResult?.type !== "complete"}
              usageMeasures={appConfig?.definition.usage?.measures ?? null}
            />
          ) : activeTab === "access" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {!app.fresh && (
                <AccessMapPanel
                  nodeId={app.id}
                  noun="connector"
                  guard={(go) => {
                    if (canWrite && isDirty && !formIsValid()) return;
                    requestClose(go);
                  }}
                />
              )}
              <AccessTab
                app={app as unknown as Parameters<typeof AccessTab>[0]["app"]}
                canManage={accessCanManage}
                canManagePersonalUse={canShare}
                stagedGrantsRef={stagedGrantsRef}
                onEdit={() => setIsDirty(true)}
                localGrants={requestMode ? (requestGrants ?? []) : undefined}
                onChangeLocalGrants={
                  requestMode
                    ? (grants) => {
                        setRequestGrants(grants);
                        setRequestGrantsEdited(true);
                      }
                    : undefined
                }
                newPrincipalIds={newPrincipalIds}
                requestReason={
                  showCompose ? (
                    <NoteInput
                      label="Request reason"
                      helperText="Optional. Helps an admin understand why this access is needed."
                      placeholder="Explain why you’d like to share this connector…"
                      maxLength={GRANT_REQUEST_NOTE_MAX_LENGTH}
                      value={requestNote}
                      onChange={setRequestNote}
                    />
                  ) : pendingRequest?.note ? (
                    <NoteInput
                      label="Request reason"
                      helperText="Included with this share request."
                      maxLength={GRANT_REQUEST_NOTE_MAX_LENGTH}
                      value={pendingRequest.note}
                      readOnly
                    />
                  ) : null
                }
              />
            </div>
          ) : hasSettings && appConfig ? (
            <>
              {app.authorizationMode === "visitor" &&
                !visitorAuthorizationSupported && (
                  <Banner>
                    This connector does not support individual accounts. Turn
                    off “Require each person to connect their own account” to
                    edit its configuration.
                  </Banner>
                )}
              {canWrite &&
                app.fresh &&
                connectorVariants(appConfig).length > 1 && (
                  <VariantPicker appId={app.id} appConfig={appConfig} />
                )}
              {/* Keyed by variant so switching remounts the form's uncontrolled
                  inputs — a secret typed for the old variant would otherwise
                  linger on screen after the row's secrets are cleared. */}
              <Form
                key={appConfig.identifier}
                definition={appConfig.definition}
                showInstructions={guideOn}
                app={app}
                disabled={!canWrite}
                onEdit={() => setIsDirty(true)}
                locallySet={locallySet}
                onFieldSet={onFieldSet}
                auth={auth}
              />
              {showVisitorAuthorization && (
                <VisitorAuthorizationSection
                  appId={app.id}
                  providerName={appConfig.definition.name}
                  authorizationMode={app.authorizationMode}
                  grants={app.visitorGrants ?? []}
                  loading={appResult?.type !== "complete"}
                  canWrite={canWrite}
                />
              )}
              {!draft && canWrite && (
                <DangerSection
                  style={{ marginTop: 16 }}
                  title="Delete connector"
                  description="Permanently deletes this connector and its configuration. Any steps that use it will need a replacement."
                  actionLabel="Delete connector"
                  renderConfirm={({ open, onClose: closeConfirm }) =>
                    open ? (
                      <Modal open onClose={closeConfirm} alert>
                        <DeleteConnectorModalBody
                          appId={app.id}
                          onClose={closeConfirm}
                          onDeleted={() => {
                            skipCatalogCleanup.current = true;
                            onClose();
                          }}
                        />
                      </Modal>
                    ) : null
                  }
                />
              )}
            </>
          ) : null}
        </Body>
        <Footer>
          <SecureBadge />
          <FooterRight>
            {!showPendingReview && (
              <ConnectorStatusPill app={app} showDetails />
            )}
            {requestMode ? (
              showPendingReview ? (
                isRequester ? (
                  <>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Pending approval
                    </Text>
                    <Button variant="subtle" size="l" onClick={cancelRequest}>
                      Cancel request
                    </Button>
                  </>
                ) : (
                  <>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      Requested by {requesterName}
                    </Text>
                    <Button
                      variant="subtle"
                      size="l"
                      onClick={() => setDecliningRequest(pendingRequest.id)}
                    >
                      Decline
                    </Button>
                    <Button variant="primary" size="l" onClick={approveRequest}>
                      Approve
                    </Button>
                  </>
                )
              ) : proposesShare ? (
                <Button
                  variant="primary"
                  size="l"
                  onClick={() => requestClose(submitRequest)}
                >
                  Request approval
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="l"
                  onClick={finish}
                  disabled={auth.isPending}
                >
                  {canWrite && app.fresh ? "Save" : "Done"}
                </Button>
              )
            ) : canWrite && app.fresh && !visitedAccess ? (
              <Button variant="primary" size="l" onClick={goToAccess}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                size="l"
                onClick={finish}
                disabled={auth.isPending}
              >
                {canWrite && app.fresh ? "Save" : "Done"}
              </Button>
            )}
          </FooterRight>
        </Footer>
      </Modal>
      {decliningRequest && (
        <DeclineGrantRequestModal
          key={decliningRequest}
          requestId={decliningRequest}
          connectorName={app.name}
          onClose={() => setDecliningRequest(null)}
          onDeclined={onClose}
        />
      )}
      {closeWarningModal}
      {authorizePrompt && appConfig && (
        <Modal open onClose={() => setAuthorizePrompt(false)} alert>
          <WarningContent>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Text variant="text-regular-md" color="var(--text-primary)">
                Authorize now?
              </Text>
              <Text variant="text-regular" color="var(--text-secondary)">
                This connector will be saved as a draft until it’s authorized
                with {appConfig.definition.name}.
              </Text>
            </div>
            <WarningFooter>
              <Button
                size="l"
                variant="secondary"
                onClick={() => {
                  setAuthorizePrompt(false);
                  publishAndClose();
                }}
              >
                Save as draft
              </Button>
              <Button size="l" onClick={authorizeAndClose}>
                Authorize
              </Button>
            </WarningFooter>
          </WarningContent>
        </Modal>
      )}
      <ConfirmModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={discardDraft}
        title="Discard new connection?"
        description="Your changes will be lost if you continue."
        confirmLabel="Discard"
        destructive
      />
    </>
  );
}
